import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Shield, Trash2, Plus, Phone, Edit, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { permissions } from '@/lib/mockAuth';
import { authService } from '@/services/auth.service';
import { weddingService } from '@/services/wedding.service';
import { inviteService } from '@/services/invite.service';
import InviteMemberDialog from '@/components/InviteMemberDialog';
import { toast } from 'sonner';
import EnhancedAppHeader from '@/components/EnhancedAppHeader';

export default function OrganizationSettings() {
  const navigate = useNavigate();
  const { activeOrg, userRole, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [selectedWeddings, setSelectedWeddings] = useState([]);
  const [allWeddings, setAllWeddings] = useState([]);
  const [loadingWeddings, setLoadingWeddings] = useState(false);

  const canManage = permissions.canManageMembers(userRole);

  useEffect(() => {
    if (activeOrg) {
      loadMembers();
      loadWeddings();
    }
  }, [activeOrg]);

  const loadWeddings = async () => {
    if (!activeOrg?.id) return;
    
    try {
      const data = await weddingService.getWeddings(activeOrg.id);
      setAllWeddings(data);
    } catch (error) {
      console.error('Failed to load weddings:', error);
    }
  };

  const loadMembers = async () => {
    if (!activeOrg?.id) return;
    
    try {
      const orgMembers = await authService.getTeamMembers(activeOrg.id);
      // Transform API response to match expected format
      const transformedMembers = orgMembers.map(member => ({
        id: member.id,
        userId: member.isInvite ? null : member.id, // Invites don't have userId yet
        inviteId: member.isInvite ? member.id : null, // Track invite ID for invites
        name: member.name,
        email: member.email,
        phone: member.phone || '',
        role: member.role,
        status: member.status || 'active', // Use status from API (active, invited, etc.)
        isInvite: member.isInvite || false
      }));
      setMembers(transformedMembers);
    } catch (error) {
      console.error('Failed to load team members:', error);
      toast.error('Failed to load team members');
    }
  };

  const isOrgCreator = (member) => {
    return member.userId === activeOrg?.ownerId;
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await authService.updateMemberRole(memberId, activeOrg.id, newRole);
      loadMembers();
      toast.success('Role updated successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update role';
      toast.error(errorMessage);
    }
  };

  const handleEditClick = async (member) => {
    // Don't allow editing invites
    if (member.isInvite) {
      toast.info('Cannot edit pending invites. Please wait for them to accept or cancel the invite.');
      return;
    }
    
    setEditingMember(member);
    setEditForm({
      name: member.name,
      email: member.email,
      phone: member.phone || ''
    });
    
    // Load wedding assignments for planner/client
    if (member.role === 'planner' || member.role === 'client') {
      try {
        setLoadingWeddings(true);
        const weddings = await authService.getMemberWeddings(member.userId, activeOrg.id);
        setSelectedWeddings(weddings.map(w => w.id));
      } catch (error) {
        console.error('Failed to load member weddings:', error);
        setSelectedWeddings([]);
      } finally {
        setLoadingWeddings(false);
      }
    } else {
      setSelectedWeddings([]);
    }
  };

  const handleEditSave = async () => {
    if (!editingMember || !activeOrg?.id) return;
    
    try {
      // Update member details (name, phone, and email if user has permission)
      if (editingMember.userId) {
        const updateData = {
          name: editForm.name,
          phone: editForm.phone
        };
        
        // Only include email if user has permission to change it and email has changed
        if (canManage && editForm.email !== editingMember.email) {
          updateData.email = editForm.email;
        }
        
        await authService.updateTeamMember(
          editingMember.userId,
          activeOrg.id,
          updateData
        );
      }
      
      // Update wedding assignments if role is planner/client
      if ((editingMember.role === 'planner' || editingMember.role === 'client') && editingMember.userId) {
        await authService.assignMemberToWeddings(
          editingMember.userId,
          activeOrg.id,
          selectedWeddings
        );
        toast.success('Member details and wedding assignments updated successfully');
      } else {
        toast.success('Member details updated successfully');
      }
      
      setEditingMember(null);
      setSelectedWeddings([]);
      loadMembers();
    } catch (error) {
      console.error('Failed to update member:', error);
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleWeddingToggle = (weddingId) => {
    const isClient = editingMember?.role === 'client';
    
    if (isClient) {
      // Clients can only select one wedding - replace selection
      setSelectedWeddings(prev.includes(weddingId) ? [] : [weddingId]);
    } else {
      // Planners can select multiple weddings
      setSelectedWeddings(prev => 
        prev.includes(weddingId)
          ? prev.filter(id => id !== weddingId)
          : [...prev, weddingId]
      );
    }
  };

  const handleResendInvite = async (member) => {
    if (!member.isInvite || !activeOrg?.id) return;

    try {
      await inviteService.resendInvite(member.inviteId, activeOrg.id);
      toast.success(`Invite resent successfully to ${member.email}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to resend invite';
      toast.error(errorMessage);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove || !activeOrg?.id) return;

    try {
      // Check if it's an invite or an active member
      if (memberToRemove.isInvite) {
        // Cancel the invite
        await inviteService.cancelInvite(memberToRemove.inviteId, activeOrg.id);
        toast.success('Invite cancelled successfully');
      } else {
        // Remove active member
        await authService.removeTeamMember(memberToRemove.userId, activeOrg.id);
        toast.success('Member removed successfully');
      }
      loadMembers();
      setMemberToRemove(null);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to remove member';
      toast.error(errorMessage);
      setMemberToRemove(null);
    }
  };

  const handleInviteSent = (email, role) => {
    loadMembers();
    setIsInviteDialogOpen(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'org_admin': return 'bg-primary text-primary-foreground';
      case 'planner': return 'bg-accent text-accent-foreground';
      case 'read_only_client': return 'bg-secondary text-secondary-foreground';
      default: return 'bg-muted';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success text-primary-foreground';
      case 'invited': return 'bg-warning text-foreground';
      case 'disabled': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted';
    }
  };

  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">No organization selected</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <EnhancedAppHeader />
      
      <div className="container mx-auto p-4 sm:p-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text">Team Members</h1>
            <p className="text-sm text-muted-foreground mt-1">{activeOrg.name}</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Team Members ({members.length})
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your organization's team and their roles
              </p>
            </div>
            {canManage && (
              <Button
                onClick={() => setIsInviteDialogOpen(true)}
                className="gradient-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            )}
          </div>

          {/* Members Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Name</th>
                    <th className="text-left p-4 font-semibold">Email</th>
                    <th className="text-left p-4 font-semibold">Phone Number</th>
                    <th className="text-left p-4 font-semibold">Role</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    {canManage && <th className="text-right p-4 font-semibold">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {members.map((member) => (
                    <tr key={member.userId} className="hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="font-medium">{member.name}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {member.email}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {member.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="p-4">
                        {isOrgCreator(member) ? (
                          <Badge className={getRoleColor('org_admin')}>
                            <Shield className="h-3 w-3 mr-1" />
                            Admin (Creator)
                          </Badge>
                        ) : canManage && member.status === 'active' && !member.isInvite ? (
                          <Select
                            value={member.role}
                            onValueChange={(value) => handleRoleChange(member.userId, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="org_admin">Org Admin</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="planner">Planner</SelectItem>
                              <SelectItem value="client">Client</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={getRoleColor(member.role)}>
                            {member.role.replace('_', ' ')}
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge className={getStatusColor(member.status)}>
                          {member.status === 'invited' ? 'Invited' : member.status === 'active' ? 'Active' : member.status}
                        </Badge>
                      </td>
                      {canManage && (
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Show resend invite button for pending invites */}
                            {member.isInvite && member.status === 'invited' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResendInvite(member)}
                                className="hover:bg-primary/10"
                                title="Resend invite"
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Show edit button only for active members (not invites) */}
                            {!member.isInvite && !isOrgCreator(member) && member.role !== 'org_admin' && member.userId !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(member)}
                                className="hover:bg-primary/10"
                                title="Edit member"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Show remove/cancel button for invites and members (except org creator and self) */}
                            {!isOrgCreator(member) && member.role !== 'org_admin' && member.userId !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setMemberToRemove(member)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                title={member.isInvite ? "Cancel invite" : "Remove member"}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {members.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No team members yet</p>
            </div>
          )}
        </Card>
      </div>

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onInviteSent={handleInviteSent}
        organizationId={activeOrg?.id}
      />

      {/* Edit Member Dialog */}
      <Dialog open={!!editingMember} onOpenChange={() => {
        setEditingMember(null);
        setSelectedWeddings([]);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1.5"
                disabled
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-1.5"
                disabled={!canManage}
              />
              {!canManage && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  Only organization admins and admins can change email addresses.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone Number</Label>
              <Input
                id="edit-phone"
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="mt-1.5"
                placeholder="+91 98765 43210"
              />
            </div>

            {/* Wedding Assignment for Planner/Client */}
            {editingMember && (editingMember.role === 'planner' || editingMember.role === 'client') && (
              <div>
                <Label>
                  Assign to {editingMember.role === 'client' ? 'Wedding' : 'Weddings'} *
                  {editingMember.role === 'client' && (
                    <span className="text-xs text-muted-foreground ml-2">(Clients can only be assigned to one wedding)</span>
                  )}
                </Label>
                {loadingWeddings ? (
                  <div className="mt-1.5 p-4 text-center text-sm text-muted-foreground">
                    Loading weddings...
                  </div>
                ) : allWeddings.length === 0 ? (
                  <Alert className="mt-1.5 border-warning/20 bg-warning/5">
                    <AlertDescription className="text-sm">
                      No weddings found. Please create a wedding first.
                    </AlertDescription>
                  </Alert>
                ) : editingMember.role === 'client' ? (
                  // Client: Single selection dropdown
                  <Select
                    value={selectedWeddings[0] || ''}
                    onValueChange={(value) => setSelectedWeddings(value ? [value] : [])}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select a wedding" />
                    </SelectTrigger>
                    <SelectContent>
                      {allWeddings.map((wedding) => (
                        <SelectItem key={wedding.id} value={wedding.id}>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium">
                              {wedding.bride_name} & {wedding.groom_name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({wedding.wedding_date})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  // Planner: Multiple selection checkboxes
                  <div className="mt-1.5 max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2">
                    {allWeddings.map((wedding) => (
                      <div key={wedding.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-wedding-${wedding.id}`}
                          checked={selectedWeddings.includes(wedding.id)}
                          onCheckedChange={() => handleWeddingToggle(wedding.id)}
                        />
                        <Label
                          htmlFor={`edit-wedding-${wedding.id}`}
                          className="flex-1 cursor-pointer text-sm font-normal"
                        >
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium">
                              {wedding.bride_name} & {wedding.groom_name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({wedding.wedding_date})
                            </span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
                {selectedWeddings.length > 0 && editingMember.role === 'planner' && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {selectedWeddings.length} wedding(s) selected
                  </p>
                )}
                {selectedWeddings.length === 0 && allWeddings.length > 0 && (
                  <Alert className="mt-1.5 border-warning/20 bg-warning/5">
                    <AlertDescription className="text-sm">
                      Please select at least one wedding for {editingMember.role === 'planner' ? 'planner' : 'client'}.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditingMember(null);
              setSelectedWeddings([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSave} 
              className="gradient-primary"
              disabled={
                editingMember && 
                (editingMember.role === 'planner' || editingMember.role === 'client') &&
                selectedWeddings.length === 0
              }
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {memberToRemove?.isInvite ? 'Cancel Invite?' : 'Remove Team Member?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {memberToRemove?.isInvite ? (
                <>
                  Are you sure you want to cancel the invite for <strong>{memberToRemove?.name}</strong> ({memberToRemove?.email})?
                  They will not be able to join {activeOrg?.name} using this invite link.
                </>
              ) : (
                <>
                  Are you sure you want to remove <strong>{memberToRemove?.name}</strong> from {activeOrg?.name}?
                  They will lose access to all weddings and data.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {memberToRemove?.isInvite ? 'Cancel Invite' : 'Remove Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
